/* eslint-disable prettier/prettier */
//
import PropTypes from "prop-types"
import { Component } from "react"

import { createCustomFilterGroup } from "shared/search/containers/FilterGroup/CustomFilterGroup"
import { createOrderByFilterGroup, genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"
import { createGrassrootsActivityGroup } from "shared/search/containers/FilterGroup/GrassrootsActivityFilterGroup"
import { createProjectFilterGroup } from "shared/search/containers/FilterGroup/ProjectFilterGroup"
import { createStakeholderFilterGroup } from "shared/search/containers/FilterGroup/StakeholderFilterGroup"
import { createSupporterPACFilterGroup } from "shared/search/containers/FilterGroup/SupporterPACFilterGroup"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { hasAnyPermission, checkPermission } from "shared/imports/permissionFunctions"

import { usCanadaEURegion } from "shared/imports/regionConstants"

import {
    AssignedToMeFilter,
    AssignedToSupporterFilter,
    AssignedToUserFilter,
    ClickedLinkOutboxEmailFilter,
    DidNotClickLinkOutboxEmailFilter,
    DidNotOpenOutboxEmailFilter,
    DidNotReplyOutboxEmailFilter,
    HasAssignmentTripletFilter,
    ManuallyChangedInListFilter,
    NotAssignedToMeFilter,
    NotAssignedToSupporterFilter,
    NotAssignedToUserFilter,
    OpenedOutboxEmailFilter,
    PriorityFilter,
    PriorityLastUserFilter,
    // emails
    ReceivedOutboxEmailFilter,
    RepliedOutboxEmailFilter,
    RepresentingCountryAllFilter,
    RepresentingCountyFilter,
    RepresentingDistrictFilter,
    RepresentingLowerLDFilter,
    RepresentingMunicipalityFilter,
    // geography
    RepresentingStateFilter,
    RepresentingUpperLDFilter,
    RepresentingZipCodeFilter,
    UnsubscribedEmailFilter,
    createListFilter,
    createNotInListFilter,
} from "shared/search/containers/Filter/Filters"

import {
    ActionCenterInteractionsFilter,
    ActionCenterSourceFilter,
    AdvocateIsActiveFilter,
    AdvocatesNumActionsAggregateFilter,
    AdvocatesNumCampaignsInteractedFilter,
    RegisteredWithTextKeywordFilter,
    RegistrationFormInteractionsFilter,
    RegistrationFormSourceFilter,
    SubscribedTextFilter,
    SupporterAssigneeOfBillFilter,
    SupporterAssigneeOfOfficialFilter,
    SupporterAttendedCustomEventFilter,
    SupporterCampaignFilter,
    SupporterCampaignFinanceHasContributedFilter,
    SupporterCampaignFinanceLegislatorFilter,
    SupporterCampaignFinanceReceiptsContributorCommitteesFilter,
    SupporterCreatedDateEndFilter,
    SupporterCreatedDateListFilter,
    SupporterCreatedDateStartFilter,
    // custom events
    SupporterCustomEventFilter,
    SupporterFromBulkUploadFilter,
    SupporterHasBeenSentSpecificTextFilter,
    SupporterHasEverBeenSentTextFilter,
    SupporterHasNameFilter,
    SupporterHasNotRsvpCustomEventFilter,
    SupporterHasOpenedTextFilter,
    SupporterHasPhoneNumberFilter,
    SupporterSearchByCollaboratorFilter,
    SupporterHasReceivedEmailFilter,
    SupporterHasRsvpMaybeCustomEventFilter,
    SupporterHasRsvpNoCustomEventFilter,
    SupporterHasRsvpYesCustomEventFilter,
    SupporterInteractedInGrassrootsActionFilter,
    SupporterInvitedToEventCustomEventFilter,
    SupporterIsGrassrootsContactFilter,
    SupporterIsStakeholderContactFilter,
    SupporterLastActionDateFilter,
    SupporterLastKindActionFilter,
    SupporterLastModifiedByFilter,
    SupporterLegislatorFilter,
    SupporterNotCampaignFilter,
    SupporterOrganizationsFilter,
    // Organization profile
    SupporterOrganizatonsFilter,
    SupporterParticipatedInAnyCampaignFilter,
    SupporterPhoneNumberFilter,
    SupporterRecentlyActiveEmailFilter,
    SupporterSecondaryPhoneNumberFilter,
    SupporterSharedAnyCampaignOnSocialFilter,
    SupporterSourceFilter,
    SupporterSubmittedAnyFacebookLead,
    SupporterTargetedByCampaignFilter,
    SupporterTextDateSentFilter,
    SupporterTextSentAfterFilter,
    SupporterTextSentBeforeFilter,
    SupporterTitleFilter,
    SupporterUTMSourceFilter,
    SupporterUniqueIdentifierFilter,
    SupporterUpdatedDateEndFilter,
    SupporterUpdatedDateListFilter,
    SupporterUpdatedDateStartFilter,
    SupporterValidEmailFilter,
} from "shared/search/containers/Filter/SupporterFilters"

import { bulkUploadIsAvailable } from "shared/search/helperFunctions"
import {
    ReceiptsAmountMaxFilter,
    ReceiptsAmountMinFilter,
    ReceiptsDateEndFilter,
    ReceiptsDateStartFilter,
} from "../Filter/ReceiptsFilter"
import { isGrassrootsPersona } from "shared/grassroots/helperFunctions"

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

export const SupporterCustomFilterGroup = createCustomFilterGroup(
    "supporterCustomFilterGroup",
    DjangIO.app.models.QuorumDataType.supporter.value,
    [SupporterIsGrassrootsContactFilter, SupporterIsStakeholderContactFilter],
)

export class SupporterOrganizationFilterGroup extends Component {
    static defaultProps = {
        filters: [SupporterOrganizationsFilter],
        uniqueFilterGroupId: "supporterOrganizationFilterGroup",
        displayLabel: "Organization",
        helpText: "Filter contacts by the organization that they work for.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterSearchFilterGroup extends Component {
    static defaultProps = {
        filters: [
            SupporterHasNameFilter,
            SupporterValidEmailFilter,
            SupporterHasPhoneNumberFilter,
            ...(isFeatureEnabled("ff_contributor_license_addon") &&
            hasAnyPermission([DjangIO.app.models.PermissionType.collaborators])
                ? [SupporterSearchByCollaboratorFilter]
                : []),
            SupporterTitleFilter,
            SupporterPhoneNumberFilter,
            SupporterSecondaryPhoneNumberFilter,
            SupporterUniqueIdentifierFilter,
        ],
        uniqueFilterGroupId: "supporterSearchFilterGroup",
        displayLabel: "Contact Details",
        helpText: "Filter contacts by their name, title, email, or phone number.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterGeographyFilterGroup extends Component {
    static defaultProps = {
        filters: [
            RepresentingStateFilter,
            RepresentingDistrictFilter,
            RepresentingUpperLDFilter,
            RepresentingLowerLDFilter,
            RepresentingCountyFilter,
            RepresentingMunicipalityFilter,
            RepresentingCountryAllFilter,
            RepresentingZipCodeFilter,
        ],
        uniqueFilterGroupId: "supporterRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter contacts by the state or region that they live in.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const SupporterAssignmentFilterGroup = (props) => genericFilterGroup(props)
SupporterAssignmentFilterGroup.propTypes = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}
SupporterAssignmentFilterGroup.defaultProps = {
    filters: [
        AssignedToUserFilter,
        AssignedToSupporterFilter,
        AssignedToMeFilter,
        NotAssignedToUserFilter,
        NotAssignedToSupporterFilter,
        NotAssignedToMeFilter,
        HasAssignmentTripletFilter,
        SupporterAssigneeOfOfficialFilter,
        SupporterAssigneeOfBillFilter,
    ],
    uniqueFilterGroupId: "assignmentFilterGroup",
    displayLabel: "Assignments",
    helpText: "Filter results by which member of your team, if any, has been assigned to them.",
}
export class SupporterAssigneeOfFilterGroup extends Component {
    static defaultProps = {
        filters: [SupporterAssigneeOfOfficialFilter, SupporterAssigneeOfBillFilter],
        uniqueFilterGroupId: "supporterAssigneeOfFilterGroup",
        displayLabel: "Assignee Of",
        helpText: "Filter contacts by the what they are the assignees of.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterListFilterGroup extends Component {
    static defaultProps = {
        filters: [
            createListFilter(DjangIO.app.models.QuorumDataType.supporter.value),
            createNotInListFilter(DjangIO.app.models.QuorumDataType.supporter.value),
            ManuallyChangedInListFilter,
        ],
        uniqueFilterGroupId: "supporterListFilterGroup",
        displayLabel: "Lists",
        helpText: "Filter contacts by the lists you've saved to Projects.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterLegislatorsFilterGroup extends Component {
    static defaultProps = {
        filters: [SupporterLegislatorFilter],
        permissions: [DjangIO.app.models.PermissionType.legislators],
        uniqueFilterGroupId: "supporterLegislatorsFilterGroup",
        displayLabel: "Represented By",
        regions: usCanadaEURegion,
        helpText: "Filter contacts by the officials that represent them.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const SupporterCampaignFilterGroup = createGrassrootsActivityGroup(
    "supporterCampaignFilterGroup",
    DjangIO.app.models.QuorumDataType.supporter.value,
    [
        SupporterCampaignFilter,
        SupporterNotCampaignFilter,
        SupporterParticipatedInAnyCampaignFilter,
        SupporterSharedAnyCampaignOnSocialFilter,
        SupporterInteractedInGrassrootsActionFilter,
        SupporterTargetedByCampaignFilter,
        SupporterSubmittedAnyFacebookLead,
        ActionCenterSourceFilter,
        ActionCenterInteractionsFilter,
        RegistrationFormSourceFilter,
        RegistrationFormInteractionsFilter,
        RegisteredWithTextKeywordFilter,
        ...(checkPermission(DjangIO.app.models.PermissionType.grassroots) ? [SupporterUTMSourceFilter] : []),
        ...(isGrassrootsPersona()
            ? [
                  AdvocateIsActiveFilter,
                  AdvocatesNumActionsAggregateFilter,
                  AdvocatesNumCampaignsInteractedFilter,
                  SupporterLastKindActionFilter,
                  SupporterLastActionDateFilter,
              ]
            : []),
    ],
)

export class SupporterDateFilterGroup extends Component {
    static defaultProps = {
        filters: [
            SupporterUpdatedDateListFilter,
            SupporterUpdatedDateStartFilter,
            SupporterUpdatedDateEndFilter,
            SupporterCreatedDateListFilter,
            SupporterCreatedDateStartFilter,
            SupporterCreatedDateEndFilter,
        ],
        uniqueFilterGroupId: "supporterDateFilterGroup",
        displayLabel: "Date",
        helpText:
            "Filter for contacts that were created or updated after a particular date " +
            "or within a specific date range.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterOutboxFilterGroup extends Component {
    static defaultProps = {
        filters: [
            SupporterRecentlyActiveEmailFilter,
            SupporterHasReceivedEmailFilter,
            UnsubscribedEmailFilter,
            SupporterValidEmailFilter,
            ReceivedOutboxEmailFilter,
            OpenedOutboxEmailFilter,
            DidNotOpenOutboxEmailFilter,
            ClickedLinkOutboxEmailFilter,
            DidNotClickLinkOutboxEmailFilter,
            RepliedOutboxEmailFilter,
            DidNotReplyOutboxEmailFilter,
        ],
        uniqueFilterGroupId: "supporterOutboxFilterGroup",
        displayLabel: "Outbox Emails",
        helpText:
            "Filter for contacts based on the emails you've sent them via Outbox over the last 12 months - " +
            "whether they've opened, clicked, or replied to your emails.",
        permissions: [DjangIO.app.models.PermissionType.custom_outbox],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterTextFilterGroup extends Component {
    static defaultProps = {
        filters: [
            SubscribedTextFilter,
            SupporterHasEverBeenSentTextFilter,
            SupporterHasBeenSentSpecificTextFilter,
            SupporterHasOpenedTextFilter,
            SupporterTextDateSentFilter,
            SupporterTextSentAfterFilter,
            SupporterTextSentBeforeFilter,
        ],
        uniqueFilterGroupId: "supporterTextFilterGroup",
        displayLabel: "Text Messages",
        helpText: "Filter for contacts based on text message interactions",
        permissions: [DjangIO.app.models.PermissionType.outbound_texting],
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class SupporterDataMaintenanceFilterGroup extends Component {
    static defaultProps = {
        filters: [
            SupporterLastModifiedByFilter,
            SupporterSourceFilter,
            ...(bulkUploadIsAvailable(DjangIO.app.custom_data.types.BulkUploadType.supporter)
                ? [SupporterFromBulkUploadFilter]
                : []),
        ].filter((item) => item),
        uniqueFilterGroupId: "supporterDataMaintenanceFilterGroup",
        displayLabel: "Data Maintenance",
        helpText: "Filter contacts by contact source and last modified by user.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const SupporterOrganizationProfileFilterGroup = (props) => genericFilterGroup(props)
SupporterOrganizationProfileFilterGroup.propTypes = defaultProps
SupporterOrganizationProfileFilterGroup.defaultProps = {
    filters: [SupporterOrganizatonsFilter],
    uniqueFilterGroupId: "supporterOrganizationProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const SupporterProjectFilterGroup = createProjectFilterGroup("supporterProjectFilterGroup")
export const SupporterStakeholderFilterGroup = createStakeholderFilterGroup("supporterStakeholderFilterGroup")

export const SupporterCustomEventFilterGroup = (props) => genericFilterGroup(props)
SupporterCustomEventFilterGroup.propTypes = defaultProps
SupporterCustomEventFilterGroup.defaultProps = {
    filters: [
        SupporterCustomEventFilter,
        SupporterInvitedToEventCustomEventFilter,
        SupporterHasNotRsvpCustomEventFilter,
        SupporterHasRsvpYesCustomEventFilter,
        SupporterHasRsvpNoCustomEventFilter,
        SupporterHasRsvpMaybeCustomEventFilter,
        SupporterAttendedCustomEventFilter,
    ],
    permissions: [DjangIO.app.models.PermissionType.custom_events],
    uniqueFilterGroupId: "supporterCustomEventFilterGroup",
    displayLabel: "Events",
    helpText: "Filter contacts by the event that they are participating in.",
}

export const SupporterCampaignFinanceFilterGroup = (props) => genericFilterGroup(props)
SupporterCampaignFinanceFilterGroup.propTypes = defaultProps
SupporterCampaignFinanceFilterGroup.defaultProps = {
    filters: [
        SupporterCampaignFinanceHasContributedFilter,
        SupporterCampaignFinanceReceiptsContributorCommitteesFilter,
        SupporterCampaignFinanceLegislatorFilter,
        ReceiptsAmountMinFilter,
        ReceiptsAmountMaxFilter,
        ReceiptsDateStartFilter,
        ReceiptsDateEndFilter,
    ],
    permissions: [DjangIO.app.models.PermissionType.campaign_finance],
    userPermissions: [DjangIO.app.models.PermissionType.can_access_campaign_finance],
    uniqueFilterGroupId: "supporterCampaignFinanceFilterGroup",
    displayLabel: "Campaign Finance",
    helpText: "Filter contacts by their contributions.",
}

export const SupporterPACFilterGroup = createSupporterPACFilterGroup("supporterPACFilterGroup")

export class SupporterPriorityFilterGroup extends Component {
    static defaultProps = {
        filters: [PriorityFilter, PriorityLastUserFilter],
        uniqueFilterGroupId: "supporterPriorityFilterGroup",
        displayLabel: "Priority",
        helpText: "Filter contacts by your organization's indicated priority.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const SupporterOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "supporterOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.supporter.value,
})
